import { useState, useCallback, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import {
  getProfessionsWithPricing,
  updateProfessionPricing,
  updateAdminProfessions,
  deleteAdminProfession,
} from "../../../services/professionService";
import { FilterState } from "../types/profession.types";

export const useProfessions = () => {
  const [professions, setProfessions] = useState<any[]>([]);
  const [filteredProfessions, setFilteredProfessions] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState<number | null>(null);
  const [edits, setEdits] = useState<Record<number, any>>({});
  
  // ✅ Utiliser une ref pour éviter les mises à jour inutiles
  const professionsRef = useRef<any[]>([]);

  const fetchProfessions = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getProfessionsWithPricing();
      
      const sortedData = [...data].sort((a, b) => {
        if (a.is_priority && !b.is_priority) return -1;
        if (!a.is_priority && b.is_priority) return 1;
        return a.name.localeCompare(b.name);
      });
      
      // ✅ Vérifier si les données ont vraiment changé
      if (JSON.stringify(sortedData) !== JSON.stringify(professionsRef.current)) {
        setProfessions(sortedData);
        setFilteredProfessions(sortedData);
        professionsRef.current = sortedData;
      }
      
      setEdits({});
    } catch (error) {
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProfessions();
  }, [fetchProfessions]);

  const handleSave = async (profession: any) => {
    const edit = edits[profession.id] || {};
    
    try {
      setSavingId(profession.id);
      await updateProfessionPricing(profession.id, {
        premium_price: edit.premium_price ?? profession.premium_price,
        max_premium_slots: edit.max_premium_slots ?? profession.max_premium_slots,
        export_price: edit.export_price ?? profession.export_price,
      });

      if (edit.is_priority !== undefined && edit.is_priority !== profession.is_priority) {
        await updateAdminProfessions([{
          id: profession.id,
          name: profession.name,
          is_priority: edit.is_priority,
          max_free_slots: profession.max_free_slots,
          max_premium_slots: edit.max_premium_slots ?? profession.max_premium_slots,
          premium_price: edit.premium_price ?? profession.premium_price,
          export_price: edit.export_price ?? profession.export_price,
        }]);
      }

      toast.success(`${profession.name} mis à jour`);
      await fetchProfessions();
    } catch (error) {
      toast.error("Erreur lors de la sauvegarde");
    } finally {
      setSavingId(null);
    }
  };

  const handleDelete = async (id: number, name: string) => {
    if (!window.confirm(`Supprimer "${name}" ?`)) return;
    
    try {
      await deleteAdminProfession(id);
      toast.success(`${name} supprimée`);
      await fetchProfessions();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const applyFilters = (filters: FilterState) => {
    let filtered = [...professions];

    if (filters.searchText) {
      filtered = filtered.filter(item =>
        item.name.toLowerCase().includes(filters.searchText.toLowerCase())
      );
    }

    if (filters.priorityFilter !== "all") {
      filtered = filtered.filter(item => 
        filters.priorityFilter === "priority" ? item.is_priority : !item.is_priority
      );
    }

    if (filters.userFilter !== "all") {
      filtered = filtered.filter(item => {
        switch (filters.userFilter) {
          case "zero": return item.userCount === 0;
          case "few": return item.userCount > 0 && item.userCount < 5;
          case "many": return item.userCount >= 5;
          default: return true;
        }
      });
    }

    if (filters.premiumPriceFilter !== "all") {
      filtered = filtered.filter(item => {
        const price = item.premium_price;
        switch (filters.premiumPriceFilter) {
          case "free": return !price || price <= 0;
          case "low": return price !== null && price > 0 && price < 200;
          case "medium": return price !== null && price >= 200 && price < 400;
          case "high": return price !== null && price >= 400;
          default: return true;
        }
      });
    }

    if (filters.exportPriceFilter !== "all") {
      filtered = filtered.filter(item => {
        const price = item.export_price;
        switch (filters.exportPriceFilter) {
          case "free": return !price || price <= 0;
          case "paid": return price !== null && price > 0;
          default: return true;
        }
      });
    }

    // ✅ Éviter les mises à jour inutiles
    if (JSON.stringify(filtered) !== JSON.stringify(filteredProfessions)) {
      setFilteredProfessions(filtered);
    }
  };

  return {
    professions,
    filteredProfessions,
    loading,
    savingId,
    edits,
    setEdits,
    fetchProfessions,
    refreshProfessions: fetchProfessions, 
    handleSave,
    handleDelete,
    applyFilters,
  };
};